"use strict";
/**
 * Connection helper utilities to abstract differences between Legacy Connection and Kit Rpc
 *
 * These helpers allow code to work transparently with both:
 * - Legacy Connection from @solana/web3.js
 * - Kit Rpc from @solana/kit
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getLatestBlockhash = getLatestBlockhash;
exports.sendRawTransaction = sendRawTransaction;
exports.sendTransactionHelper = sendTransactionHelper;
var index_1 = require("../types/index");
var errors_1 = require("@hermis/errors");
/**
 * Get the latest blockhash from a connection (Legacy or Kit)
 *
 * Abstracts the difference between:
 * - Legacy: connection.getLatestBlockhash(commitment) returns { blockhash, lastValidBlockHeight }
 * - Kit: connection.getLatestBlockhash({ commitment }).send() returns { value: { blockhash, lastValidBlockHeight } }
 *
 * @param connection - Either Legacy Connection or Kit Rpc
 * @param commitment - Optional commitment level
 * @returns Promise resolving to { blockhash, lastValidBlockHeight }
 */
function getLatestBlockhash(connection, commitment) {
    return __awaiter(this, void 0, Promise, function () {
        var result;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(0, index_1.isKitConnection)(connection)) return [3 /*break*/, 2];
                    return [4 /*yield*/, connection.getLatestBlockhash({ commitment: commitment }).send()];
                case 1:
                    result = _a.sent();
                    return [2 /*return*/, result.value];
                case 2: return [4 /*yield*/, connection.getLatestBlockhash(commitment)];
                case 3: 
                // Legacy Connection: returns { blockhash, lastValidBlockHeight }
                return [2 /*return*/, _a.sent()];
            }
        });
    });
}
/**
 * Send a raw transaction (as bytes) to the network
 *
 * Abstracts the difference between:
 * - Legacy: connection.sendRawTransaction(bytes, options) returns signature string
 * - Kit: connection.sendTransaction(bytes, options).send() returns signature string
 *
 * @param connection - Either Legacy Connection or Kit Rpc
 * @param bytes - Serialized transaction bytes
 * @param options - Optional send options (skipPreflight, etc.)
 * @returns Promise resolving to transaction signature
 */
function sendRawTransaction(connection, bytes, options) {
    return __awaiter(this, void 0, Promise, function () {
        var result;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(0, index_1.isKitConnection)(connection)) return [3 /*break*/, 2];
                    return [4 /*yield*/, connection.sendTransaction(bytes, options).send()];
                case 1:
                    result = _a.sent();
                    return [2 /*return*/, result];
                case 2: return [4 /*yield*/, connection.sendRawTransaction(bytes, options)];
                case 3: 
                // Legacy Connection: sendRawTransaction(bytes, options)
                return [2 /*return*/, _a.sent()];
            }
        });
    });
}
/**
 * Send a transaction object to the network (helper for internal use)
 *
 * Abstracts the difference between:
 * - Legacy: connection.sendTransaction(transaction) accepts Transaction objects
 * - Kit: connection.sendTransaction(bytes) requires pre-serialized bytes
 *
 * Note: For Kit, the transaction must have a serialize() method
 * Note: This is named sendTransactionHelper to avoid conflicts with the main sendTransaction function
 *
 * @param connection - Either Legacy Connection or Kit Rpc
 * @param transaction - Transaction object (must have serialize() method for Kit)
 * @param options - Optional send options
 * @returns Promise resolving to transaction signature
 */
function sendTransactionHelper(connection, transaction, options) {
    return __awaiter(this, void 0, Promise, function () {
        var bytes, bytes;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(0, index_1.isKitConnection)(connection)) return [3 /*break*/, 2];
                    // Kit requires serialized bytes
                    if (typeof transaction.serialize !== 'function') {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__TRANSACTION__SERIALIZATION_FAILED, {
                            transactionType: typeof transaction,
                            reason: 'Transaction must have a serialize() method for Kit connections'
                        });
                    }
                    bytes = transaction.serialize();
                    return [4 /*yield*/, sendRawTransaction(connection, bytes, options)];
                case 1: return [2 /*return*/, _a.sent()];
                case 2:
                    // Legacy connection: use sendRawTransaction for already-signed transactions
                    // This avoids signature conflicts when the transaction is already signed
                    if (typeof transaction.serialize !== 'function') {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__TRANSACTION__SERIALIZATION_FAILED, {
                            transactionType: typeof transaction,
                            reason: 'Transaction must have a serialize() method'
                        });
                    }
                    bytes = transaction.serialize();
                    return [4 /*yield*/, connection.sendRawTransaction(bytes, options)];
                case 3: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
